import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../config/constants";
import { GetProtocolData } from "../sections/getProtocolData";
import { isOnlyRGSICUVideoProtocol, getSessionsWithSomeHitOrError } from "../functions";
// Components
import {
  CurrentResponsiveImg,
  InfoButton,
  InfoText,
  InfoArrowImg,
  IndicatorMainDiv,
  IndicatorDiv,
  ProtocolVideoButton,
  ProtocolVideoButtonResponsive,
  ButtonAndImgDiv,
  VideoButtonImg,
  VideoText,
  CurrentImg,
  IndicatorTextDiv,
  IndicatorText,
  IndicatorTitle,
  IndicatorDataDiv,
  IndicatorDataText,
} from "./styles";
import Loader from "../../loader";
import UseFetch from "../../../config/useFetch";
import PatientNavigation from "../../patientNavigation";
import MenuNavigation from "../sections/menuNavigation";
import ActivityNavigation from "./sections/activityNavigation";
import Title from "../../title";
import ProtocolVideoPopup from "../../protocolVideoPopup";
import SessionDurationPlot from "./sections/sessionDurationPlot";
import SuccessRatePlot from "./sections/successRatePlot";
import HitsAndErrorsPlot from "./sections/hitsAndErrorsPlot";
// Assets
import IcoVideo from "../../../assets/icons/ico_video.svg";
import ArrowDown from "../../../assets/icons/ico_arrow_down_black.svg";
import ArrowUp from "../../../assets/icons/ico_arrow_up_black.svg";

const Reports = () => {
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [protocolTitle, setProtocolTitle] = useState("");
  const [protocolDescription, setProtocolDescription] = useState("");
  const [protocolImg, setProtocolImg] = useState("");
  const [protocolVideo, setProtocolVideo] = useState("");
  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [isShowInfo, setIsShowInfo] = useState(false);

  const { patientId, protocolId } = useParams();
  const [t] = useTranslation("global");

  // Fetching sessions
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/icu/get-sessions/${patientId}`)
      .then(({ data }) => {
        const sessionsWithSomeHitOrError = getSessionsWithSomeHitOrError(data);
        const protocolSessions = sessionsWithSomeHitOrError.filter((session) => session.PROTOCOL_ID === Number(protocolId));
        setSessions(protocolSessions);
        setIsLoadingSessions(false);
      })
      .catch((error) => {
        console.log("Get sessions: ", error);
      });

    return () => {
      setSessions([]);
      setIsLoadingSessions(true);
    };
  }, [patientId, protocolId]);

  // Set protocol info
  useEffect(() => {
    const { reportImg, name, video, description } = GetProtocolData(protocolId);

    setProtocolTitle(t(`protocols_${name}`));
    setProtocolDescription(t(`protocols_${description}`));
    setProtocolImg(reportImg);
    setProtocolVideo(t(`protocols_${video}`));

    return () => {
      setProtocolTitle("");
      setProtocolDescription("");
      setProtocolImg("");
      setProtocolVideo("");
    };
  }, [protocolId, t]);

  const handleShowProtocolVideo = () => setIsProtocolVideoShowed(true);

  const renderButtonExtraInfo = () => {
    if (protocolVideo) {
      return (
        <ProtocolVideoButtonResponsive onClick={handleShowProtocolVideo} responsiveDisplay>
          <CurrentResponsiveImg src={protocolImg} alt="protocol-img" />
          <ButtonAndImgDiv>
            <VideoButtonImg src={IcoVideo} alt="ico-video" />
            <VideoText>{t("reports_videoText")}</VideoText>
          </ButtonAndImgDiv>
        </ProtocolVideoButtonResponsive>
      );
    }
    return <CurrentResponsiveImg src={protocolImg} alt="protocol-img" allBorder />;
  };

  const renderExtraProtocolInfo = () => {
    const handleShowInfo = () => setIsShowInfo(!isShowInfo);
    return (
      <>
        <InfoButton onClick={handleShowInfo}>
          <InfoText>{t("informationButton_title")}</InfoText>
          <InfoArrowImg src={isShowInfo ? ArrowUp : ArrowDown} alt="ico-arrow" />
        </InfoButton>
        <IndicatorMainDiv display={isShowInfo ? "flex" : "none"}>
          <IndicatorDiv>
            {protocolVideo ? (
              <ProtocolVideoButton onClick={handleShowProtocolVideo}>
                <CurrentImg src={protocolImg} alt="protocol-img" />
                <ButtonAndImgDiv>
                  <VideoButtonImg src={IcoVideo} alt="ico-video" />
                  <VideoText>{t("reports_videoText")}</VideoText>
                </ButtonAndImgDiv>
              </ProtocolVideoButton>
            ) : (
              <CurrentImg src={protocolImg} alt="protocol-img" allBorder />
            )}
            <IndicatorTextDiv>
              <IndicatorText>
                <IndicatorTitle>{t("protocols_descriptionTitle")}: </IndicatorTitle>
                {protocolDescription}
              </IndicatorText>
            </IndicatorTextDiv>
          </IndicatorDiv>
        </IndicatorMainDiv>
      </>
    );
  };

  const renderProtocolVideoPopup = () => {
    if (isProtocolVideoShowed) {
      return <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />;
    }
  };

  return (
    <>
      {isLoadingSessions ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={"icu"} />
          <MenuNavigation />
          <ActivityNavigation />
          <Title title={`${t("reports").toUpperCase()}: ${protocolTitle}`} marginBottom="10px" />
          {renderButtonExtraInfo()}
          {renderExtraProtocolInfo()}
          {renderProtocolVideoPopup()}
          {!Boolean(sessions.length) ? (
            <IndicatorDataDiv>
              <IndicatorDataText>{t("rgsicu_noSessionsMessage")}</IndicatorDataText>
            </IndicatorDataDiv>
          ) : (
            <>
              <SessionDurationPlot
                title={isOnlyRGSICUVideoProtocol(protocolId) ? t("playbackTime") : t("activityTime")}
                sessions={sessions}
              />
              {!isOnlyRGSICUVideoProtocol(protocolId) && (
                <>
                  <SuccessRatePlot sessions={sessions} />
                  <HitsAndErrorsPlot sessions={sessions} />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Reports;
